import { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Edit, Trash2, X, Send, Eye, Download, Copy, Check, DollarSign, Mail, Printer, Pen, Repeat, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import SignaturePad from '../components/SignaturePad';
import { generateInvoicePDF } from '../utils/PDFGenerator';
import {
  formatInvoiceDate as formatDate,
  calculateInvoiceTotals,
  CLEANING_SERVICES,
  getNextDocumentNumber,
  isDocumentNumberTaken,
} from '../utils/invoiceHelpers';
import InvoiceFormModal from '../components/invoices/InvoiceFormModal';
import InvoicePreviewModal from '../components/invoices/InvoicePreviewModal';
import InvoiceStatCards from '../components/invoices/InvoiceStatCards';
import InvoicesToolbar from '../components/invoices/InvoicesToolbar';
import InvoicesTable from '../components/invoices/InvoicesTable';
import CustomerDrawer from '../components/invoices/CustomerDrawer';
import RecurringInvoices from './RecurringInvoices';
import Estimates from './Estimates';
import useCloudSync from '../hooks/useCloudSync';
import { IconTile, TINT } from '../components/ui/IconTile';
import {
  sendInvoiceReminder,
  sendInvoiceEmail,
  runAutoScan,
  shouldRunAutoScan,
} from '../services/reminderService';
import { refreshOpenStatuses } from '../services/deliveryTracking';

// Stable motion prop refs so the framer-motion `whileHover` / `whileTap`
// objects on the Create Invoice CTA don't get a new reference every render
// (was a code-review finding — inline objects trigger unnecessary work).
const CTA_HOVER = { y: -2 };
const CTA_TAP = { scale: 0.97 };

const Invoices = () => {
  // ?tab=recurring or ?tab=estimates deep-links the matching sub-view.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam === 'recurring' ? 'recurring'
                  : tabParam === 'estimates' ? 'estimates'
                  : 'invoices';
  const [activeTab, setActiveTab] = useState(initialTab);
  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'invoices') setSearchParams({}, { replace: true });
    else setSearchParams({ tab }, { replace: true });
  };

  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);

  // Persist invoices + broadcast so any sibling tab (Estimates, Recurring,
  // Customer drawers) re-reads localStorage without a page refresh. Any code
  // path that writes `maidly_invoices` MUST go through this helper so cross-
  // tab sync stays consistent.
  const saveInvoices = (next, meta = {}) => {
    setInvoices(next);
    localStorage.setItem('maidly_invoices', JSON.stringify(next));
    try {
      window.dispatchEvent(new CustomEvent('maidly:invoices-updated', {
        detail: { source: 'invoices-tab', ...meta },
      }));
    } catch (_) { /* ignore */ }
  };
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const invoicePreviewRef = useRef(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailContent, setEmailContent] = useState({ subject: '', body: '', customerEmail: '' });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState({ key: 'createdAt', dir: 'desc' });
  const [drawerCustomerId, setDrawerCustomerId] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 720,
  );
  // Bumps whenever delivery statuses change so the InvoicesTable re-reads
  // the localStorage badge for each row.
  const [, setDeliveryTick] = useState(0);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 720);
    window.addEventListener('resize', onResize);
    const onDeliveryUpdate = () => setDeliveryTick((t) => t + 1);
    window.addEventListener('maidly:delivery-updated', onDeliveryUpdate);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('maidly:delivery-updated', onDeliveryUpdate);
    };
  }, []);
  const [showCustomerSignaturePad, setShowCustomerSignaturePad] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    name: 'Maidly Business Manager',
    address: '',
    phone: '',
    email: 'billing@maidly.com',
    logo: ''
  });
  const [companySignature, setCompanySignature] = useState('');

  // Predefined cleaning services + tax math now live in `utils/invoiceHelpers`
  // to keep this component focused on state + handlers. Local aliases preserve
  // the original variable names used throughout the file.
  const cleaningServices = CLEANING_SERVICES;
  const calculateTotals = calculateInvoiceTotals;
  
  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    customerContactName: '',
    customerAddress: '',
    customerEmail: '',
    customerPhone: '',
    invoiceNumber: '',
    poNumber: '',
    items: [{ id: crypto.randomUUID(), description: '', quantity: 1, rate: 0, amount: 0 }],
    subtotal: 0,
    tax: 0,
    taxRate: 8,
    discount: 0,
    total: 0,
    status: 'draft',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    notes: '',
    terms: 'Payment due within 15 days',
    footerNote: '',
    depositAmount: 0,
    recurring: false,
    recurringFrequency: 'monthly'
  });
  // Default sales tax rate from Settings → Tax Settings.
  // Loaded by loadCompanySettings(), used by resetForm() and new-invoice opens
  // so settings changes propagate to fresh invoices.
  const [defaultTaxRate, setDefaultTaxRate] = useState(8);
  const [payment, setPayment] = useState({
    amount: '',
    method: 'credit_card',
    date: new Date().toISOString().split('T')[0],
    note: ''
  });

  useEffect(() => {
    loadData();
    loadCompanySettings();
    // Refresh open-tracking status for any invoice we've emailed so the UI
    // shows the latest Sent/Opened badge. Failures are silent.
    refreshOpenStatuses().catch(() => {});
    // Run reminder auto-scan once per calendar day, non-blocking.
    if (shouldRunAutoScan()) {
      const settings = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
      const invoicesData = JSON.parse(localStorage.getItem('maidly_invoices') || '[]');
      const customersData = JSON.parse(localStorage.getItem('maidly_customers') || '[]');
      runAutoScan({
        invoices: invoicesData,
        customers: customersData,
        companyInfo: settings.companyInfo || {},
        settings,
      }).then((summary) => {
        if (summary.sent > 0) {
          toast.success(
            `Sent ${summary.sent} payment reminder${summary.sent === 1 ? '' : 's'}`,
          );
        }
      }).catch((e) => {
        console.warn('[reminders] auto-scan failed', e);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only loaders
  }, []);

  // When the user converts an Estimate → Invoice (in the sibling tab) we
  // dispatch a `maidly:invoices-updated` event. Listening here means the
  // freshly-created invoice shows up the instant they switch tabs, instead
  // of requiring a full page refresh.
  useEffect(() => {
    const refresh = () => loadData();
    window.addEventListener('maidly:invoices-updated', refresh);
    // Also reload when customers were edited elsewhere (e.g. address added
    // in the Customers tab). Without this, the invoice form's "No address"
    // warning would keep firing for a customer that already has one.
    window.addEventListener('maidly:customers-updated', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('maidly:invoices-updated', refresh);
      window.removeEventListener('maidly:customers-updated', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  // Also reload whenever the user re-enters the Invoices sub-tab, in case
  // they converted from Estimates while the Recurring tab was active.
  useEffect(() => {
    if (activeTab === 'invoices') loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on tab switch
  }, [activeTab]);

  // Realtime: refresh when other devices push invoice/customer changes
  useCloudSync((evt) => {
    loadData();
    // Subtle pulse: if the update came from a teammate (not me), surface it
    const by = evt?.detail?.by_user;
    const myId = JSON.parse(localStorage.getItem('maidly_current_user') || '{}')?.id;
    if (by && myId && by !== myId) {
      const types = evt?.detail?.data_types || [];
      if (types.includes('invoices')) {
        toast('Invoice updated by teammate', { description: 'List refreshed' });
      }
    }
  }, ['invoices', 'customers']);

  // Cross-tab tax rate sync — refresh defaultTaxRate + current form when
  // the user saves Settings elsewhere.
  useEffect(() => {
    const refresh = () => {
      try {
        const s = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
        const tr = Number(s.taxRate ?? s.salesTaxRate);
        if (Number.isFinite(tr)) {
          setDefaultTaxRate(tr);
          setFormData((f) => ({ ...f, taxRate: tr }));
        }
        if (s?.companyInfo) setCompanyInfo(s.companyInfo);
      } catch (_) { /* ignore */ }
    };
    window.addEventListener('maidly:settings-updated', refresh);
    return () => window.removeEventListener('maidly:settings-updated', refresh);
  }, []);

  // Cross-page action handoff. The Customer History modal (from the
  // Customers tab) drops an action payload into `sessionStorage` and
  // navigates here — we drain it once the invoices list is loaded so the
  // right modal opens for the picked invoice.
  useEffect(() => {
    if (!invoices.length) return;
    let payload = null;
    try {
      const raw = sessionStorage.getItem('maidly_invoice_action');
      if (raw) payload = JSON.parse(raw);
    } catch { /* ignore */ }
    if (!payload || !payload.invoiceId) return;
    sessionStorage.removeItem('maidly_invoice_action');

    const invoice = invoices.find((i) => i.id === payload.invoiceId);
    if (!invoice) return;

    switch (payload.action) {
      case 'edit':
        handleEdit(invoice);
        break;
      case 'pay':
        if (invoice.status !== 'paid') {
          setSelectedInvoice(invoice);
          setShowPaymentModal(true);
        }
        break;
      case 'email':
        sendEmail(invoice);
        break;
      case 'reminder':
        handleSendReminder(invoice);
        break;
      case 'duplicate':
        duplicateInvoice(invoice);
        break;
      case 'delete':
        handleDelete(invoice.id);
        break;
      case 'preview':
      default:
        setPreviewInvoice(enrichInvoice(invoice));
        setShowPreviewModal(true);
        break;
    }
    // Only run when the invoices list settles for the first time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices.length]);

  const sendEmail = (invoice) => {
    // Prepare email content
    const customer = customers.find(c => c.id === invoice.customerId);
    const customerEmail = customer?.email || '';
    
    const subject = `Invoice #${invoice.invoiceNumber} from ${companyInfo.name || 'Maidly Business Manager'}`;
    const body = `Dear ${invoice.customerName},

Thank you for your business! Please find your invoice details below:

Invoice Number: #${invoice.invoiceNumber}
Invoice Date: ${formatDate(invoice.date)}
Due Date: ${formatDate(invoice.dueDate)}
Total Amount: $${(invoice.total || 0).toFixed(2)}

${invoice.status === 'unpaid' ? `This invoice is currently unpaid. Payment is due by ${formatDate(invoice.dueDate)}.` : ''}
${invoice.status === 'partial' ? `This invoice has a partial payment of $${(invoice.paidAmount || 0).toFixed(2)}. Remaining balance: $${((invoice.total || 0) - (invoice.paidAmount || 0)).toFixed(2)}.` : ''}
${invoice.status === 'paid' ? 'Thank you for your payment! This invoice has been paid in full.' : ''}

📎 A PDF copy of this invoice is attached for your records.

If you have any questions about this invoice, please don't hesitate to contact us.

Best regards,
${companyInfo.name || 'Maidly Business Manager'}
${companyInfo.phone || ''}
${companyInfo.email || ''}`;

    // Store email content, invoice, and show modal
    setEmailContent({ subject, body, customerEmail });
    setSelectedInvoice(invoice);
    setShowEmailModal(true);
  };

  // Enrich an invoice with the current customer's contact info before
  // rendering the PDF/preview. Invoices only persist customerId + name, so we
  // hydrate address / email / phone from the live customers list at render
  // time. Falls back gracefully if the customer was deleted.
  const enrichInvoice = (invoice) => {
    if (!invoice) return invoice;
    const customer = customers.find((c) => c.id === invoice.customerId);
    return {
      ...invoice,
      customerName: invoice.customerName || customer?.name || '',
      customerAddress: invoice.customerAddress || customer?.address || '',
      customerEmail: invoice.customerEmail || customer?.email || '',
      customerPhone: invoice.customerPhone || customer?.phone || '',
      footerNote:
        invoice.footerNote
        || companyInfo.invoiceFooterNote
        || 'Thank you for supporting my small business!',
    };
  };

  const printInvoice = (invoice) => {
    // Generate PDF and open in new window for printing (no preview modal)
    generateInvoicePDF(enrichInvoice(invoice), companyInfo);
    toast.success('Invoice PDF generated! Use your PDF viewer to print.');
  };

  const downloadInvoicePDF = (invoice) => {
    generateInvoicePDF(enrichInvoice(invoice), companyInfo);
    toast.success('Invoice PDF downloaded!');
  };

  // Download PDF and open email client together
  const downloadPDFAndOpenEmail = () => {
    if (selectedInvoice) {
      // First download the PDF
      generateInvoicePDF(enrichInvoice(selectedInvoice), companyInfo);
      toast.success('Invoice PDF downloaded! Please attach it to your email.');
      
      // Then open email client after a short delay
      setTimeout(() => {
        openInvoiceEmailClient();
      }, 500);
    }
  };

  const copyInvoiceEmail = () => {
    const fullContent = `To: ${emailContent.customerEmail}\nSubject: ${emailContent.subject}\n\n${emailContent.body}`;
    navigator.clipboard.writeText(fullContent).then(() => {
      toast.success('Email content copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  const openInvoiceEmailClient = () => {
    try {
      const mailtoLink = `mailto:${emailContent.customerEmail}?subject=${encodeURIComponent(emailContent.subject)}&body=${encodeURIComponent(emailContent.body)}`;
      
      const anchor = document.createElement('a');
      anchor.href = mailtoLink;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      
      setTimeout(() => {
        document.body.removeChild(anchor);
      }, 100);
      
      toast.success('Opening email client...');
    } catch (error) {
      console.error('Error opening email client:', error);
      toast.error('Failed to open email client. Please use "Copy to Clipboard" instead.');
    }
  };

  const loadData = () => {
    const invoicesData = JSON.parse(localStorage.getItem('maidly_invoices') || '[]');
    setInvoices(invoicesData);
    const customersData = JSON.parse(localStorage.getItem('maidly_customers') || '[]');
    setCustomers(customersData);
  };

  const loadCompanySettings = () => {
    const settings = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
    if (settings.companyInfo) {
      setCompanyInfo(settings.companyInfo);
    }
    if (settings.companySignature) {
      setCompanySignature(settings.companySignature);
    }
    // Apply default sales tax rate from Settings → Tax Settings to the invoice form,
    // but only when the user isn't currently editing an existing invoice (whose own
    // taxRate must be preserved).
    const settingsTaxRate = Number(settings.taxRate ?? settings.salesTaxRate);
    if (Number.isFinite(settingsTaxRate)) {
      setDefaultTaxRate(settingsTaxRate);
      setFormData(prev => {
        if (editingInvoice) return prev;
        const totals = calculateInvoiceTotals(prev.items, settingsTaxRate, prev.discount, prev.depositAmount);
        return { ...prev, taxRate: settingsTaxRate, ...totals };
      });
    }
  };

  // Send a friendly reminder email for a single invoice.
  const handleSendReminder = async (invoice) => {
    const customer = customers.find((c) => c.id === invoice.customerId);
    const dueDate = invoice.dueDate ? new Date(invoice.dueDate) : null;
    let daysUntilDue = 0;
    if (dueDate) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const due = new Date(dueDate); due.setHours(0, 0, 0, 0);
      daysUntilDue = Math.round((due - today) / 86400000);
    }
    toast.info('Sending reminder...');
    const res = await sendInvoiceReminder({
      invoice,
      customer,
      companyInfo,
      daysUntilDue,
    });
    if (res.success) toast.success(res.message);
    else toast.error(res.message);
  };

  // Send the initial invoice email (with PDF attached) via the company's
  // own Gmail SMTP configured in Settings.
  const handleSendInvoiceNow = async (invoice, customMessage) => {
    const target = invoice || selectedInvoice;
    if (!target) return;
    const customer = customers.find((c) => c.id === target.customerId);
    toast.info('Sending invoice...');
    let pdfBase64 = null;
    try {
      pdfBase64 = generateInvoicePDF(enrichInvoice(target), companyInfo, { returnBase64: true });
    } catch (e) {
      console.warn('[send-invoice] PDF generation failed, sending without attachment', e);
    }
    const res = await sendInvoiceEmail({
      invoice: target,
      customer,
      companyInfo,
      customMessage,
      pdfBase64,
    });
    if (res.success) {
      toast.success(res.message);
      setShowEmailModal(false);
    } else {
      toast.error(res.message);
    }
  };

  const handleCustomerSignature = (signatureData) => {
    // Save customer signature to the invoice
    const updatedInvoice = {
      ...previewInvoice,
      customerSignature: signatureData,
      signatureDate: new Date().toISOString()
    };

    // Update in localStorage + broadcast so sibling tabs refresh.
    const invoicesData = JSON.parse(localStorage.getItem('maidly_invoices') || '[]');
    const updatedInvoices = invoicesData.map(inv => 
      inv.id === updatedInvoice.id ? updatedInvoice : inv
    );
    saveInvoices(updatedInvoices, { action: 'signature', invoiceId: updatedInvoice.id });
    setPreviewInvoice(updatedInvoice);
    setShowCustomerSignaturePad(false);
    
    toast.success('Invoice signed successfully!');
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = newItems[index].quantity * newItems[index].rate;
    }
    
    const totals = calculateTotals(newItems, formData.taxRate, formData.discount, formData.depositAmount);
    setFormData({
      ...formData,
      items: newItems,
      subtotal: totals.subtotal,
      tax: totals.tax,
      total: totals.total
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { id: crypto.randomUUID(), description: '', quantity: 1, rate: 0, amount: 0 }]
    });
  };

  const addCleaningService = (service) => {
    const newItem = {
      id: crypto.randomUUID(),
      description: service.description,
      quantity: 1,
      rate: service.rate,
      amount: service.rate
    };
    
    const newItems = [...formData.items];
    // Replace empty first item or add new item
    if (newItems.length === 1 && !newItems[0].description) {
      newItems[0] = newItem;
    } else {
      newItems.push(newItem);
    }
    
    const totals = calculateTotals(newItems, formData.taxRate, formData.discount, formData.depositAmount);
    setFormData({
      ...formData,
      items: newItems,
      subtotal: totals.subtotal,
      tax: totals.tax,
      total: totals.total
    });
    
    toast.success('Service added to invoice');
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    const totals = calculateTotals(newItems, formData.taxRate, formData.discount, formData.depositAmount);
    setFormData({
      ...formData,
      items: newItems,
      subtotal: totals.subtotal,
      tax: totals.tax,
      total: totals.total
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.customerId) {
      toast.error('Please select a customer');
      return;
    }
    
    if (formData.items.length === 0 || !formData.items[0].description) {
      toast.error('Please add at least one item');
      return;
    }

    if (editingInvoice) {
      const updated = invoices.map(inv => 
        inv.id === editingInvoice.id ? { ...inv, ...formData, updatedAt: new Date().toISOString() } : inv
      );
      saveInvoices(updated, { action: 'update', invoiceId: editingInvoice.id });
      toast.success('Invoice updated successfully');
    } else {
      // Collision-proof numbering: honor a user-entered number only if it's not
      // already in use; otherwise (blank OR duplicate) generate a guaranteed
      // unique next number. Fixes old invoice numbers being reused on new ones.
      const typed = (formData.invoiceNumber || '').trim();
      const invoiceNumber = (typed && !isDocumentNumberTaken(invoices, typed))
        ? typed
        : getNextDocumentNumber(invoices, 'INV');
      const newInvoice = {
        ...formData,
        id: Date.now().toString(),
        invoiceNumber,
        createdAt: new Date().toISOString(),
        payments: []
      };
      const updated = [...invoices, newInvoice];
      saveInvoices(updated, { action: 'create', invoiceId: newInvoice.id });
      toast.success('Invoice created successfully');
    }
    
    resetForm();
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      const updated = invoices.filter(inv => inv.id !== id);
      saveInvoices(updated, { action: 'delete', invoiceId: id });
      toast.success('Invoice deleted successfully');
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    const cust = customers.find(c => c.id === invoice.customerId);
    // If the invoice was created before the new customer schema, hydrate
    // the contact name from the linked customer's first/last-name fields.
    const contactName = invoice.customerContactName
      || (cust ? [cust.contactFirstName, cust.contactLastName].filter(Boolean).join(' ').trim() : '');
    setFormData({
      customerId: invoice.customerId,
      customerName: invoice.customerName,
      customerContactName: contactName,
      customerAddress: invoice.customerAddress || cust?.address || '',
      customerEmail: invoice.customerEmail || cust?.email || '',
      customerPhone: invoice.customerPhone || cust?.phone || '',
      invoiceNumber: invoice.invoiceNumber || '',
      poNumber: invoice.poNumber || '',
      items: invoice.items,
      subtotal: invoice.subtotal,
      tax: invoice.tax,
      taxRate: invoice.taxRate || 8,
      discount: invoice.discount || 0,
      total: invoice.total,
      status: invoice.status,
      date: invoice.date || (invoice.createdAt ? invoice.createdAt.split('T')[0] : new Date().toISOString().split('T')[0]),
      dueDate: invoice.dueDate,
      notes: invoice.notes || '',
      terms: invoice.terms || 'Payment due within 15 days',
      footerNote: invoice.footerNote || '',
      depositAmount: invoice.depositAmount || 0,
      recurring: invoice.recurring || false,
      recurringFrequency: invoice.recurringFrequency || 'monthly'
    });
    setShowModal(true);
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    // Combine the new first/last-name fields into a single display line.
    // Falls back to a blank string when either half is missing so BILL TO
    // stays clean.
    const contactName = customer
      ? [customer.contactFirstName, customer.contactLastName].filter(Boolean).join(' ').trim()
      : '';
    setFormData({
      ...formData,
      customerId,
      customerName: customer ? customer.name : '',
      customerContactName: contactName,
      // Auto-populate contact fields from the selected customer so they land
      // in BILL TO on the preview + PDF. User can still edit per-invoice.
      customerAddress: customer?.address || '',
      customerEmail: customer?.email || '',
      customerPhone: customer?.phone || '',
    });
  };

  const handleAddPayment = () => {
    if (!payment.amount || parseFloat(payment.amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    const paymentAmount = parseFloat(payment.amount);
    const invoice = invoices.find(inv => inv.id === selectedInvoice.id);
    const totalPaid = (invoice.payments || []).reduce((sum, p) => sum + p.amount, 0) + paymentAmount;

    const newPayment = {
      id: Date.now().toString(),
      amount: paymentAmount,
      method: payment.method,
      date: payment.date,
      note: payment.note,
      createdAt: new Date().toISOString()
    };

    const newStatus = totalPaid >= invoice.total ? 'paid' : totalPaid > 0 ? 'partial' : 'pending';

    const updated = invoices.map(inv => 
      inv.id === selectedInvoice.id 
        ? { 
            ...inv, 
            payments: [...(inv.payments || []), newPayment],
            status: newStatus,
            balanceDue: invoice.total - totalPaid
          } 
        : inv
    );

    saveInvoices(updated, { action: 'payment', invoiceId: invoice.id });
    setShowPaymentModal(false);
    setPayment({ amount: '', method: 'credit_card', date: new Date().toISOString().split('T')[0], note: '' });
    toast.success('Payment recorded successfully');
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingInvoice(null);
    setFormData({
      customerId: '',
      customerName: '',
      customerContactName: '',
      customerAddress: '',
      customerEmail: '',
      customerPhone: '',
      invoiceNumber: '',
      poNumber: '',
      items: [{ id: crypto.randomUUID(), description: '', quantity: 1, rate: 0, amount: 0 }],
      subtotal: 0,
      tax: 0,
      taxRate: defaultTaxRate,
      discount: 0,
      total: 0,
      status: 'draft',
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      notes: '',
      terms: 'Payment due within 15 days',
      footerNote: '',
      depositAmount: 0,
      recurring: false,
      recurringFrequency: 'monthly'
    });
  };

  const duplicateInvoice = (invoice) => {
    const newInvoice = {
      ...invoice,
      id: Date.now().toString(),
      invoiceNumber: getNextDocumentNumber(invoices, 'INV'),
      status: 'draft',
      createdAt: new Date().toISOString(),
      payments: []
    };
    const updated = [...invoices, newInvoice];
    saveInvoices(updated, { action: 'duplicate', invoiceId: newInvoice.id });
    toast.success('Invoice duplicated successfully');
  };

  // Convert this invoice into a recurring template. Switches to the
  // Recurring sub-tab and opens the create-schedule modal with the invoice's
  // customer + line items pre-filled so the user can pick cadence, timezone,
  // payment due, etc. through the proper UI (no window.prompt).
  const handleMakeRecurring = (invoice) => {
    try {
      sessionStorage.setItem('maidly_recurring_prefill', JSON.stringify({
        customerId: invoice.customerId,
        customerName: invoice.customerName,
        customerAddress: invoice.customerAddress || '',
        customerEmail: invoice.customerEmail || '',
        customerPhone: invoice.customerPhone || '',
        items: (invoice.items || []).map((it) => ({
          description: it.description || '',
          quantity: Number(it.quantity) || 1,
          rate: Number(it.rate) || 0,
          amount: Number(it.amount || (Number(it.quantity) || 1) * (Number(it.rate) || 0)),
        })),
        tax_rate: Number(invoice.taxRate) || 0,
        discount: Number(invoice.discount) || 0,
        deposit_amount: Number(invoice.depositAmount) || 0,
        notes: invoice.notes || '',
        terms: invoice.terms || '',
        source_invoice_id: invoice.id,
        source_invoice_number: invoice.invoiceNumber,
      }));
    } catch (_) { /* ignore quota */ }
    switchTab('recurring');
    // Wake the Recurring page in case it was already mounted (a subsequent
    // click after cancelling the modal would otherwise leave stale sessionStorage).
    try {
      window.dispatchEvent(new CustomEvent('maidly:recurring-prefill', {
        detail: { source: 'invoice', invoiceId: invoice.id },
      }));
    } catch (_) { /* ignore */ }
  };

  // Compute derived list: status filter + search + sort
  const visibleInvoices = useMemo(() => {
    let list = invoices;
    if (filterStatus !== 'all') {
      if (filterStatus === 'overdue') {
        const now = new Date();
        list = list.filter((inv) => {
          if (inv.status === 'paid' || inv.status === 'draft') return false;
          return inv.dueDate && new Date(inv.dueDate) < now;
        });
      } else {
        list = list.filter((inv) => inv.status === filterStatus);
      }
    } else {
      // Default "All" view hides fully-paid invoices — they live in the
      // "Paid" filter and in each customer's history modal instead.
      list = list.filter((inv) => inv.status !== 'paid');
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (inv) =>
          (inv.invoiceNumber || '').toLowerCase().includes(q) ||
          (inv.customerName || '').toLowerCase().includes(q),
      );
    }
    const sorted = list.slice().sort((a, b) => {
      const dir = sortBy.dir === 'asc' ? 1 : -1;
      const k = sortBy.key;
      if (k === 'total') return ((a.total || 0) - (b.total || 0)) * dir;
      if (k === 'dueDate') {
        const da = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const db = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return (da - db) * dir;
      }
      // default: createdAt
      const da = new Date(a.createdAt || 0).getTime();
      const db = new Date(b.createdAt || 0).getTime();
      return (da - db) * dir;
    });
    return sorted;
  }, [invoices, filterStatus, search, sortBy]);

  const filteredInvoices = visibleInvoices; // keep legacy name in scope
  const toggleSort = (key) => {
    setSortBy((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
    );
  };

  const statusCounts = useMemo(() => {
    // `all` = open invoices only (matches the new default filter, which
    // excludes fully paid ones — those still count in the `paid` chip).
    const counts = { all: 0, draft: 0, pending: 0, partial: 0, paid: 0, overdue: 0 };
    const now = new Date();
    invoices.forEach((inv) => {
      if (inv.status !== 'paid') counts.all += 1;
      if (inv.status && counts[inv.status] !== undefined) counts[inv.status] += 1;
      if (inv.status !== 'paid' && inv.status !== 'draft' && inv.dueDate && new Date(inv.dueDate) < now) {
        counts.overdue += 1;
      }
    });
    return counts;
  }, [invoices]);

  return (
    <div data-testid="invoices-page">
      {/* Tabs: Invoices ↔ Estimates ↔ Recurring — matches Time Clock / Employees / Inspections tab bar */}
      <div
        role="tablist"
        aria-label="Invoice views"
        style={{
          display: 'inline-flex', gap: 6, padding: 5, marginBottom: 16,
          background: '#f1f5f9', borderRadius: 12, border: '1px solid #e2e8f0',
          boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.04)',
        }}
      >
        {[
          { id: 'invoices', label: 'Invoices', icon: DollarSign, tint: TINT.invoices },
          { id: 'estimates', label: 'Estimates', icon: FileText, tint: TINT.recurring },
          { id: 'recurring', label: 'Recurring', icon: Repeat, tint: TINT.reports },
        ].map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              data-testid={`invoices-tab-${t.id}`}
              onClick={() => switchTab(t.id)}
              style={{
                all: 'unset', cursor: 'pointer',
                padding: '8px 18px 8px 8px', borderRadius: 9,
                fontSize: 13, fontWeight: 700, letterSpacing: 0.1,
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: isActive ? '#0073ea' : 'transparent',
                color: isActive ? '#ffffff' : '#475569',
                boxShadow: isActive ? '0 3px 10px rgba(0,115,234,0.28)' : 'none',
                transition: 'background 0.18s, color 0.18s, box-shadow 0.18s',
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(15,23,42,0.05)'; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <IconTile icon={Icon} tint={isActive ? 'rgba(255,255,255,0.22)' : t.tint} size={28} iconSize={15} />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'recurring' ? (
        <RecurringInvoices />
      ) : activeTab === 'estimates' ? (
        <Estimates />
      ) : (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <IconTile icon={DollarSign} tint={TINT.invoices} size={44} />
            Invoices
          </h1>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            {invoices.length} total · {statusCounts.overdue} overdue · realtime
          </div>
        </div>
        <motion.button
          whileHover={CTA_HOVER}
          whileTap={CTA_TAP}
          onClick={() => {
            // Pre-populate the next invoice number so it's visible in the
            // form on open. User can still edit it before saving.
            const nextNumber = getNextDocumentNumber(invoices, 'INV');
            setFormData((prev) => ({ ...prev, invoiceNumber: nextNumber }));
            setShowModal(true);
          }}
          data-testid="add-invoice-button"
          style={{
            all: 'unset', cursor: 'pointer',
            padding: '0.55rem 1rem 0.55rem 0.55rem',
            background: TINT.invoices, color: '#fff', borderRadius: 12,
            fontSize: '0.9rem', fontWeight: 700, letterSpacing: 0.1,
            display: 'inline-flex', alignItems: 'center', gap: 8,
            boxShadow: `0 6px 16px ${TINT.invoices}55`,
            transition: 'background 0.2s ease, box-shadow 0.2s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = '#16a34a';
            e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.invoices}88`;
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = TINT.invoices;
            e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.invoices}55`;
          }}
        >
          <IconTile icon={Plus} tint="rgba(255,255,255,0.22)" size={30} />
          Create Invoice
        </motion.button>
      </div>

      {/* Stats Cards */}
      <InvoiceStatCards
        invoices={invoices}
        activeStat={
          filterStatus === 'draft' ? 'drafts'
          : filterStatus === 'paid' ? 'paidThisMonth'
          : filterStatus === 'overdue' ? 'overdue'
          : null
        }
        onPickStat={(key) => {
          if (key === 'drafts') setFilterStatus('draft');
          else if (key === 'paidThisMonth') setFilterStatus('paid');
          else if (key === 'overdue') setFilterStatus('overdue');
          else if (key === 'upcoming') setFilterStatus('pending');
          else setFilterStatus('all');
        }}
      />

      {/* Status tabs + search */}
      <InvoicesToolbar
        filterStatus={filterStatus}
        onFilterChange={setFilterStatus}
        statusCounts={statusCounts}
        search={search}
        onSearchChange={setSearch}
      />

      {/* Invoices Table */}
      <InvoicesTable
        invoices={filteredInvoices}
        sortBy={sortBy}
        onToggleSort={toggleSort}
        onPreview={(invoice) => { setPreviewInvoice(enrichInvoice(invoice)); setShowPreviewModal(true); }}
        onEdit={(invoice) => handleEdit(invoice)}
        onPay={(invoice) => { setSelectedInvoice(invoice); setShowPaymentModal(true); }}
        onDuplicate={(invoice) => duplicateInvoice(invoice)}
        onMakeRecurring={(invoice) => handleMakeRecurring(invoice)}
        onSendEmail={(invoice) => sendEmail(invoice)}
        onSendReminder={(invoice) => handleSendReminder(invoice)}
        onPrint={(invoice) => printInvoice(invoice)}
        onDelete={(id) => handleDelete(id)}
        onCustomerClick={(customerId) => setDrawerCustomerId(customerId)}
      />

      {/* Customer Drawer */}
      <CustomerDrawer
        open={Boolean(drawerCustomerId)}
        onClose={() => setDrawerCustomerId(null)}
        customer={customers.find((c) => c.id === drawerCustomerId) || null}
        invoices={invoices}
        isMobile={isMobile}
        onCreateInvoice={(cust) => {
          setDrawerCustomerId(null);
          handleCustomerChange({ target: { value: cust.id } });
          const nextNumber = getNextDocumentNumber(invoices, 'INV');
          setFormData((prev) => ({ ...prev, invoiceNumber: nextNumber }));
          setShowModal(true);
        }}
        onOpenInvoice={(inv) => {
          setDrawerCustomerId(null);
          setPreviewInvoice(enrichInvoice(inv));
          setShowPreviewModal(true);
        }}
      />


      {/* Create/Edit Invoice Modal */}
      <InvoiceFormModal
        open={showModal}
        editingInvoice={editingInvoice}
        formData={formData}
        setFormData={setFormData}
        customers={customers}
        onClose={resetForm}
        onSubmit={handleSubmit}
        onCustomerChange={handleCustomerChange}
        onItemChange={handleItemChange}
        onAddItem={addItem}
        onAddCleaningService={addCleaningService}
        onRemoveItem={removeItem}
        calculateTotals={calculateTotals}
      />

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '1.5rem',
            width: '100%',
            maxWidth: '500px'
          }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Record Payment</h3>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
              Invoice: {selectedInvoice.invoiceNumber} - ${selectedInvoice.total.toFixed(2)}
            </p>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Amount *</label>
              <input
                type="number"
                step="0.01"
                value={payment.amount}
                onChange={(e) => setPayment({ ...payment, amount: e.target.value })}
                className="input"
                placeholder="Enter payment amount"
              />
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Payment Method *</label>
              <select
                value={payment.method}
                onChange={(e) => setPayment({ ...payment, method: e.target.value })}
                className="input"
              >
                <option value="credit_card">Credit Card</option>
                <option value="debit_card">Debit Card</option>
                <option value="ach">ACH/Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Date *</label>
              <input
                type="date"
                value={payment.date}
                onChange={(e) => setPayment({ ...payment, date: e.target.value })}
                className="input"
              />
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>Note</label>
              <textarea
                value={payment.note}
                onChange={(e) => setPayment({ ...payment, note: e.target.value })}
                className="input"
                style={{ minHeight: '60px' }}
                placeholder="Add any notes about this payment..."
              />
            </div>
            
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => { setShowPaymentModal(false); setPayment({ amount: '', method: 'credit_card', date: new Date().toISOString().split('T')[0], note: '' }); }} 
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleAddPayment} className="btn btn-primary">
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <InvoicePreviewModal
        open={showPreviewModal}
        previewInvoice={previewInvoice}
        companyInfo={companyInfo}
        companySignature={companySignature}
        printRef={invoicePreviewRef}
        onClose={() => setShowPreviewModal(false)}
        onDownload={downloadInvoicePDF}
        onRequestCustomerSignature={() => setShowCustomerSignaturePad(true)}
      />


      {/* Customer Signature Pad Modal */}
      {showCustomerSignaturePad && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '2rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            maxWidth: '700px',
            width: '100%',
            padding: '2rem'
          }}>
            <SignaturePad
              onSave={handleCustomerSignature}
              onCancel={() => setShowCustomerSignaturePad(false)}
              label={`Customer Signature - ${previewInvoice?.customerName || 'Customer'}`}
            />
          </div>
        </div>
      )}

      {/* Email Content Modal */}
      {showEmailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              position: 'sticky',
              top: 0,
              background: '#fff',
              zIndex: 1
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Email Invoice</h2>
              <button 
                onClick={() => setShowEmailModal(false)} 
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              {/* PDF Attachment Section */}
              <div style={{ 
                padding: '1rem', 
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', 
                borderRadius: '8px', 
                marginBottom: '1.5rem',
                border: '1px solid #a7f3d0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    background: '#10b981',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Download size={20} color="white" />
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', color: '#065f46' }}>PDF Invoice Attachment</div>
                    <div style={{ fontSize: '13px', color: '#047857' }}>
                      Invoice #{selectedInvoice?.invoiceNumber} • ${(selectedInvoice?.total || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleSendInvoiceNow(selectedInvoice, emailContent.body)}
                  data-testid="send-invoice-now-button"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: '#0073ea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '10px',
                  }}
                >
                  <Send size={18} />
                  Send Invoice Now (from my email)
                </button>
                <p style={{ fontSize: '12px', color: '#0369a1', marginTop: '-4px', marginBottom: '12px', textAlign: 'center' }}>
                  Delivers immediately through the sender you set up in Settings, with the invoice PDF attached.
                </p>

                <button
                  onClick={downloadPDFAndOpenEmail}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Download size={18} />
                  Download PDF & Open Email Client
                </button>
                <p style={{ fontSize: '12px', color: '#047857', marginTop: '8px', textAlign: 'center' }}>
                  The PDF will download first, then your email client will open. Attach the downloaded PDF to your email.
                </p>
              </div>

              <div style={{ 
                padding: '1rem', 
                background: '#f0f9ff', 
                borderRadius: '6px', 
                marginBottom: '1.5rem',
                fontSize: '0.875rem',
                color: '#1e40af'
              }}>
                <strong>📧 Alternative:</strong> Copy the content below and paste it into your email client, then attach the PDF manually.
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  To:
                </label>
                <div style={{ 
                  padding: '0.75rem', 
                  background: '#f9fafb', 
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  color: '#0073ea',
                  fontWeight: 600
                }}>
                  {emailContent.customerEmail || 'No email address available'}
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Subject:
                </label>
                <div style={{ 
                  padding: '0.75rem', 
                  background: '#f9fafb', 
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }}>
                  {emailContent.subject}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Message:
                </label>
                <textarea
                  value={emailContent.body}
                  readOnly
                  style={{ 
                    width: '100%',
                    minHeight: '250px',
                    padding: '0.75rem',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button 
                  onClick={() => {
                    if (selectedInvoice) {
                      generateInvoicePDF(enrichInvoice(selectedInvoice), companyInfo);
                      toast.success('Invoice PDF downloaded!');
                    }
                  }}
                  className="btn btn-secondary"
                  style={{ flex: 1, minWidth: '120px' }}
                >
                  <Download size={18} style={{ marginRight: '0.5rem' }} />
                  Download PDF Only
                </button>
                <button 
                  onClick={copyInvoiceEmail}
                  className="btn btn-primary"
                  style={{ flex: 1, minWidth: '120px' }}
                >
                  <Copy size={18} style={{ marginRight: '0.5rem' }} />
                  Copy Email
                </button>
                <button 
                  onClick={openInvoiceEmailClient}
                  className="btn btn-secondary"
                  style={{ flex: 1, minWidth: '120px' }}
                >
                  <Mail size={18} style={{ marginRight: '0.5rem' }} />
                  Open Email Only
                </button>
                <button 
                  onClick={() => setShowEmailModal(false)}
                  className="btn btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default Invoices;
